import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { useLocation, useOutletContext } from 'react-router-dom';
import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import { useRecoilValue } from 'recoil';
import { FormBuilder } from '../../../components/FormBuilder';
import { useLoader } from '../../../components/Loader/useLoader';
import { SupportPopup, FormSubmitHelper, ErrorModalSection } from '../../../components';
import { ApplicationRoutes } from '../../../Router';

import { businessVerificationValidationSchema } from './businessVerificationSchema';
import { formInitialValues, getFormFields } from './businessVerificationConstants';
import { useModal } from '../../../components/modalController/useModal';
import { mapValues, stripEmpty } from '../../../helpers';
import { findFirstValidationError } from '../../../helpers/formik';
import { useTealium } from '../../../tealium/useTealium';

import { useRecoilState } from 'recoil';
import { businessVerificationState } from '../../../recoil/verfication';
import { fundingDetailsSubmit, businessList, applicationsList } from '../../../recoil';
import { businessVerification } from '../../../api/vodaLend/businessVerification';

export const BusinessVerification = () => {
  const { submitState, setSubmitState, submissionStates, nextClick } = useOutletContext();
  const formRef = useRef();
  const location = useLocation();
  const { track, events } = useTealium();
  const [openLoader, closeLoader] = useLoader();
  const [openModal, closeModal] = useModal();
  const [fundingDetails] = useRecoilState(fundingDetailsSubmit);
  const [businessVerificationResult, setBusinessVerificationResult] = useRecoilState(businessVerificationState);
  const [businessVerificationResponse, setBusinessVerificationResponse] = useState({});
  const [isNextAllowed, setIsNextAllowed] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const businessType = fundingDetails?.businessType;
  const bussinessList = useRecoilValue(businessList);
  const appsList = useRecoilValue(applicationsList);

  useEffect(() => {
    track(events.businessVerification.pageView());
  }, []);

  const closeErrorModal = () => {
    setSubmitState(submissionStates.default);
    closeModal();
  };

  useEffect(() => {

    if (businessVerificationResponse?.data?.error) {
      setTimeout(() => {
        openModal({
          title: 'Error',
          children: <ErrorModalSection
            traceId={businessVerificationResponse?.traceId}
            error={(businessVerificationResponse?.data?.error) || null}
            onCloseErrorModal={closeErrorModal}
          />,
          type: 'error',
          onCloseModal: closeErrorModal
        });
        return;
      }, 0);
    }

  }, [isNextAllowed, businessVerificationResponse]);

  const formDetails = useMemo(() => formInitialValues, []);

  const formatFormData = (formData) => mapValues(stripEmpty(formData));

  let formTouched = false;

  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.businessVerification.formStart());
  };

  const handleNext = async () => {

    await track(events.businessVerification.formEnd());
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (businessVerificationResult?.businessId) {
      localStorage.setItem('businessId', businessVerificationResult?.businessId);
    }
    if (businessVerificationResult?.isMatched) {
      if (fundingDetails?.businessType === 'Sole Proprietorship') {
        localStorage.setItem('loanApplicationStepKey', 'financialDetails');
        window.location.pathname = ApplicationRoutes.financialDetails;
      }
      window.location.pathname = ApplicationRoutes.businessDirectors;
    } else {
      window.location.pathname = ApplicationRoutes.businessDetails;
    }
    setIsNextAllowed(true);
    setSubmitState(submissionStates.submitSuccess);
    nextClick();
  };

  const onBackClick = () => {
    if (appsList.length > 0) {
      localStorage.setItem('loanApplicationStepKey', 'personalDetails');
      window.location.pathname = ApplicationRoutes.personalDetails;
    }
    else if (bussinessList && bussinessList?.businesses.length > 0) {
      localStorage.setItem('loanApplicationStepKey', 'businessDetails');
      window.location.pathname = ApplicationRoutes.businessList;
    } else {
      localStorage.setItem('loanApplicationStepKey', 'personalDetails');
      window.location.pathname = ApplicationRoutes.personalDetails;
    }
  };

  const handleRegNumberBlur = async (values, errors) => {
    const regNumber = values?.regNumber;
    const businessname = values?.businessName;
    const vatNumber = values?.vatNumber;

    if (businessVerificationResponse && businessVerificationResponse?.data &&
      !businessVerificationResponse?.data?.error) {
      return;
    }

    if (
      !businessname ||
      (!regNumber && !vatNumber) ||
      (errors && (errors.regNumber || errors.vatNumber))
    ) {
      return;
    }

    const submission = formatFormData({
      regNumber,
      businessname,
      vatNumber,
      businessType: businessType
    });

    openLoader();

    try {
      const verificationResponse = await businessVerification(submission);
      setBusinessVerificationResponse(verificationResponse);

      if (verificationResponse?.data?.error) {
        setIsNextAllowed(false);
        setSubmitState(submissionStates.submitError);
      } else {
        setBusinessVerificationResult({
          isMatched: verificationResponse?.data?.isMatched,
          businessId: verificationResponse?.data?.businessId,
          businessName: businessname,
          regNumber,
          vatNumber
        });
        sessionStorage.removeItem('sharedBusinessDirector');
        sessionStorage.removeItem('sharedDirectors');
        sessionStorage.setItem('businessName', businessname);
        if (businessType !== 'Sole Proprietorship') {
          sessionStorage.setItem('regNumber', regNumber);
        } else {
          sessionStorage.setItem('vatNumber', vatNumber);
        }

        setShowNext(true);
      }
    } catch (error) {
      setBusinessVerificationResponse(error);
      setIsNextAllowed(false);
      setSubmitState(submissionStates.submitError);
    } finally {
      closeLoader();
    }
  };

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);
    if (errorMessage) {
      track(events.error('form', location.pathname, errorMessage));
    }
  };
  return (
    <div>
      <ThemeProvider theme={THEME.revamp}>
        <Formik
          initialValues={formDetails}
          onSubmit={() => { }}
          validate={(values) => {

            try {
              businessVerificationValidationSchema.validateSync(values, {
                abortEarly: false,
                context: { businessType }
              });
              return {};
            } catch (error) {
              return error.inner.reduce((acc, err) => {
                acc[err.path] = err.message;
                return acc;
              }, {});
            }
          }}
          validateOnBlur={true}
          validateOnMount={true}
          innerRef={formRef}
        >
          {(formik) => {
            const { values, errors, touched } = formik;

            handleFormikTouchedErrors(touched, errors);
            return (
              <Form onFocus={handleFormFocus}>
                <FormSubmitHelper submitState={submitState} setSubmitState={setSubmitState} />
                <FormBuilder
                  fields={getFormFields(businessType, businessVerificationResponse)}
                  title="Business details"
                  subtitle="Please accurately enter all your business details to streamline your funding application."
                  values={values}
                  errors={errors}
                  backClick={onBackClick}
                  nextClick={handleNext}
                  touched={touched}
                  businessVerificationResult={businessVerificationResult}
                  isNextAllowed={showNext}
                  customBlurHandlers={handleRegNumberBlur}
                />
              </Form>
            );
          }}
        </Formik>

      </ThemeProvider>
      <SupportPopup />
    </div>
  );

};